import { useRef, useState, useCallback } from 'react';

const stages = [
  { num: '01', title: 'Listen', desc: 'Understand the ground reality — people, places, and the issues that matter.' },
  { num: '02', title: 'Research', desc: 'Collect meaningful field intelligence through surveys, data and direct engagement.' },
  { num: '03', title: 'Analyse', desc: 'Convert raw data into actionable insight that shapes the right question.' },
  { num: '04', title: 'Strategise', desc: 'Build the right intervention — the plan, the message, and the system to deliver it.' },
  { num: '05', title: 'Deliver', desc: 'Execute, measure and improve. Review the outcome and close the loop.' },
];

export default function HowWeWork() {
  const scroller = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);

  const updateProgress = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const p = max > 0 ? el.scrollLeft / max : 0;
    setProgress(p);
  }, []);

  const scrollBy = (dir: number) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: 'smooth' });
  };

  return (
    <section id="how-we-work" className="relative w-full bg-background-50 py-20 md:py-28 overflow-hidden">
      <div className="px-6 md:px-16 lg:px-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[10px] tracking-[0.3em] uppercase text-foreground-500 font-mono">04 / How we work</span>
            </div>
            <h2 data-reveal className="text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.08] text-foreground-950 tracking-tight font-semibold">
              A disciplined path, <span className="text-primary-700">end to end</span>.
            </h2>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono text-foreground-500">{Math.round(progress * 100)}%</span>
            <div className="h-px w-24 bg-background-200 overflow-hidden">
              <div className="h-full bg-primary-600 transition-[width] duration-150" style={{ width: `${progress * 100}%` }}></div>
            </div>
            <button
              onClick={() => scrollBy(-1)}
              aria-label="Scroll left"
              className="w-10 h-10 rounded-full border border-background-300 flex items-center justify-center text-foreground-700 hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition cursor-pointer"
            >
              <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
            </button>
            <button
              onClick={() => scrollBy(1)}
              aria-label="Scroll right"
              className="w-10 h-10 rounded-full border border-background-300 flex items-center justify-center text-foreground-700 hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition cursor-pointer"
            >
              <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Horizontal scroller */}
      <div
        ref={scroller}
        onScroll={updateProgress}
        className="h-scroll flex gap-5 overflow-x-auto scroll-smooth px-6 md:px-16 lg:px-28 pb-4 snap-x snap-mandatory"
      >
        {stages.map((s) => (
          <article
            key={s.num}
            className="snap-start shrink-0 w-[82vw] sm:w-[440px] lg:w-[480px] rounded-2xl border border-background-200 bg-background-100/50 p-8 md:p-10 flex flex-col justify-between min-h-[300px] hover:bg-background-100 transition-colors"
          >
            <div className="flex items-start justify-between">
              <span className="text-[60px] leading-none font-light text-primary-200" style={{ fontFamily: 'var(--font-heading)' }}>
                {s.num}
              </span>
              <span className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center text-primary-700">
                <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
              </span>
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl tracking-wide text-foreground-950 font-semibold mb-3">
                {s.title}
              </h3>
              <p className="text-[14px] leading-[1.8] text-foreground-600">{s.desc}</p>
            </div>
          </article>
        ))}

        {/* End card */}
        <article className="snap-start shrink-0 w-[82vw] sm:w-[440px] lg:w-[480px] rounded-2xl bg-primary-800 p-8 md:p-10 flex flex-col justify-between min-h-[300px]">
          <span className="text-[60px] leading-none font-light text-accent-400/60" style={{ fontFamily: 'var(--font-heading)' }}>
            05+
          </span>
          <div>
            <h3 className="text-2xl md:text-3xl tracking-wide text-white font-semibold mb-3">
              Then we close the loop.
            </h3>
            <p className="text-[14px] leading-[1.8] text-white/70">
              Review, learn and improve — so every next engagement starts smarter
              than the last.
            </p>
            <a
              href="#contact"
              className="mt-6 inline-flex items-center gap-2 text-sm text-accent-400 hover:text-accent-300 transition cursor-pointer whitespace-nowrap font-mono uppercase tracking-wider"
            >
              Start a conversation <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>
        </article>
      </div>
    </section>
  );
}